import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Trans, t } from '@lingui/macro';
import { Modal } from 'features/modalManager';
import { Envelope } from '@phosphor-icons/react';
import type { ExportType } from 'features/misc/table/types';
import { Radio, RadioGroup } from 'components/inputs';
import { Text } from 'components/text';
import { useTheme } from '@emotion/react';

export interface TableExportModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (exportType: ExportType) => void;
  isLoading?: boolean;
}

export const TableExportModal = ({
  open,
  onClose,
  onExport,
  isLoading = false,
}: TableExportModalProps) => {
  const [exportType, setExportType] = useState<ExportType>('DisplayedData');
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      setExportType('DisplayedData');
    }
  }, [open]);

  const handleExport = () => {
    onExport(exportType);
  };

  const handleClose = (event: React.MouseEvent<Element, MouseEvent>, reason: string) => {
    if (reason !== 'apply') {
      onClose();
    }
  };

  const handleExportTypeChange = (value: string): void => {
    if (value === 'DisplayedData' || value === 'CompleteData') {
      setExportType(value as ExportType);
    }
  };

  return (
    <Modal
      id="table-export-modal"
      open={open}
      onClose={handleClose}
      hideDontShowAgain
      onActionsClick={(event, actionId) => {
        if (actionId === 'apply') {
          handleExport();
        }
      }}
      title={t`Export excel`}
      description={
        <Trans>
          Please select which data you would like to export. The export may take a few minutes to
          become available.
        </Trans>
      }
      actions={[
        {
          id: 'cancel',
          label: t`Cancel`,
          disabled: isLoading,
        },
        {
          id: 'apply',
          label: t`Send to my email`,
          disabled: isLoading,
          startIcon: <Envelope size={20} color="white" />,
        },
      ]}
    >
      <ExportContent>
        <RadioGroup
          name="exportType"
          value={exportType}
          onChange={e => handleExportTypeChange(e.target.value)}
        >
          <RadioContainer>
            <Radio value="DisplayedData" label={t`Current column layout`} />
            <Text left={32} color={theme.palette.text.secondary}>
              <Trans>Export only the visible data shown in the table.</Trans>
            </Text>
          </RadioContainer>
          <RadioContainer>
            <Radio value="CompleteData" label={t`All data`} />
            <Text left={32} color={theme.palette.text.secondary}>
              <Trans>Export all available data.</Trans>
            </Text>
          </RadioContainer>
        </RadioGroup>
      </ExportContent>
    </Modal>
  );
};

const ExportContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  [role='radiogroup'] {
    gap: 24px;
  }
  .MuiFormControlLabel-label {
    font-weight: 600;
  }
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
